import type { ComponentProps, ReactNode } from "react";
import { cn } from "../lib/cn";

export function Card({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn("rounded-xl border border-border/80 bg-card text-card-foreground shadow-sm", className)}
      {...props}
    />
  );
}

export interface CardHeaderProps extends ComponentProps<"div"> {
  /**
   * Buttons that belong to this card: beside the title where there is room, under it where there is
   * not. Passing them here rather than laying them out at the call site is what makes that true
   * everywhere (US-37.7).
   */
  actions?: ReactNode;
}

/**
 * The head of a card: a title, usually a line under it, and sometimes a button or three.
 *
 * <p>With {@link CardHeaderProps.actions} it becomes a toolbar, and the toolbar **stacks** below
 * `sm`. Sixty-five screens used to write `flex-row … justify-between` by hand, which on a phone
 * squeezed the title and its description into a narrow column beside a vertical stack of buttons —
 * the order list wrapped its description every two or three words (UX-14). `min-w-0` on the title
 * column is the other half of that fix: without it a long word keeps the column from ever shrinking.
 */
export function CardHeader({ className, actions, children, ...props }: CardHeaderProps) {
  if (!actions) {
    return (
      <div className={cn("flex flex-col gap-1.5 p-5 md:p-6", className)} {...props}>
        {children}
      </div>
    );
  }
  return (
    <div
      className={cn(
        // Wrapping rather than a fixed row: a card half the width of an 820 px screen with three
        // buttons in its toolbar used to squeeze the title column to a word per line. The title
        // claims a sensible basis and keeps it; the toolbar goes under it when both do not fit.
        "flex flex-col gap-3 p-5 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-x-4 md:p-6",
        className,
      )}
      {...props}
    >
      <div className="flex min-w-0 flex-col gap-1.5 sm:flex-1 sm:basis-60">{children}</div>
      <div className="flex min-w-0 flex-wrap items-center gap-2 sm:justify-end">{actions}</div>
    </div>
  );
}

export function CardTitle({ className, ...props }: ComponentProps<"h3">) {
  return <h3 className={cn("text-base font-semibold leading-none tracking-tight", className)} {...props} />;
}

export function CardDescription({ className, ...props }: ComponentProps<"p">) {
  return <p className={cn("text-sm text-muted-foreground", className)} {...props} />;
}

/**
 * The body of a card.
 *
 * <p>`pt-0` because it normally sits under a {@link CardHeader} that already paid for the top
 * padding — but since US-37.8 a card whose title moved up into the {@link PageHeader} has no
 * header at all, and then the content is the first child and needs its own top padding. The
 * `[&:first-child]` variant says exactly that, so no screen has to remember it.
 */
export function CardContent({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn("p-5 pt-0 md:p-6 md:pt-0 [&:first-child]:pt-5 md:[&:first-child]:pt-6", className)}
      {...props}
    />
  );
}

export function CardFooter({ className, ...props }: ComponentProps<"div">) {
  return <div className={cn("flex items-center p-5 pt-0 md:p-6 md:pt-0", className)} {...props} />;
}
